import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@sunfish/ui/components/form';
import { Input } from '@sunfish/ui/components/input';
import { Label } from '@sunfish/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@sunfish/ui/components/select';
import { cn } from '@sunfish/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Control, ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

// Common countries list
const COUNTRIES = [
	{ value: 'US', label: 'United States' },
	{ value: 'CA', label: 'Canada' },
	{ value: 'GB', label: 'United Kingdom' },
	{ value: 'AU', label: 'Australia' },
	{ value: 'DE', label: 'Germany' },
	{ value: 'FR', label: 'France' },
	{ value: 'IT', label: 'Italy' },
	{ value: 'ES', label: 'Spain' },
	{ value: 'MX', label: 'Mexico' },
	{ value: 'BR', label: 'Brazil' },
	{ value: 'JP', label: 'Japan' },
	{ value: 'CN', label: 'China' },
	{ value: 'IN', label: 'India' },
	{ value: 'NL', label: 'Netherlands' },
	{ value: 'SE', label: 'Sweden' },
	{ value: 'NO', label: 'Norway' },
	{ value: 'DK', label: 'Denmark' },
	{ value: 'FI', label: 'Finland' },
	{ value: 'IE', label: 'Ireland' },
	{ value: 'NZ', label: 'New Zealand' }
].sort((a, b) => a.label.localeCompare(b.label));

// Helper to parse address value
const parseAddressValue = (value: unknown): Record<string, string> => {
	if (!value) return {};
	try {
		return typeof value === 'string' ? JSON.parse(value) : (value as Record<string, string>);
	} catch {
		return {};
	}
};

// Inner component that can properly use hooks
function AddressInternationalBlockInner({
	field,
	label,
	description,
	required,
	disabled
}: {
	field: ControllerRenderProps<FieldValues, string>;
	label?: string | null;
	description?: string | null;
	required?: boolean;
	disabled?: boolean;
}) {
	// Use local state for immediate input feedback
	const [localAddress, setLocalAddress] = useState<Record<string, string>>(() => parseAddressValue(field.value));

	// Keep refs for the form sync callback
	const onChangeRef = useRef(field.onChange);
	onChangeRef.current = field.onChange;

	const requiredRef = useRef(required);
	requiredRef.current = required;

	// Sync from form to local state when field value changes externally (e.g., form reset)
	const lastFieldValueRef = useRef(field.value);
	useEffect(() => {
		if (field.value !== lastFieldValueRef.current) {
			lastFieldValueRef.current = field.value;
			setLocalAddress(parseAddressValue(field.value));
		}
	}, [field.value]);

	// Debounced sync to form - prevents validation from blocking typing
	const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingAddressRef = useRef<Record<string, string> | null>(null);

	const syncToForm = useCallback((newAddress: Record<string, string>) => {
		const hasValue = Object.values(newAddress).some((v) => v && String(v).trim().length > 0);
		const newValue = hasValue ? JSON.stringify(newAddress) : requiredRef.current ? JSON.stringify(newAddress) : '';
		lastFieldValueRef.current = newValue;
		onChangeRef.current(newValue);
	}, []);

	const debouncedSyncToForm = useCallback(
		(newAddress: Record<string, string>) => {
			pendingAddressRef.current = newAddress;
			if (syncTimeoutRef.current) {
				clearTimeout(syncTimeoutRef.current);
			}
			syncTimeoutRef.current = setTimeout(() => {
				if (pendingAddressRef.current) {
					syncToForm(pendingAddressRef.current);
					pendingAddressRef.current = null;
				}
			}, 150);
		},
		[syncToForm]
	);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (syncTimeoutRef.current) {
				clearTimeout(syncTimeoutRef.current);
			}
		};
	}, []);

	const updateAddress = useCallback(
		(key: string, value: string, isSelectField = false) => {
			setLocalAddress((prev) => {
				// Skip if value hasn't actually changed
				if (prev[key] === value) {
					return prev;
				}
				// Only for Select fields: skip if trying to clear a value that was already set (Select initialization quirk)
				if (isSelectField && value === '' && prev[key] !== undefined && prev[key] !== '') {
					return prev;
				}
				const newAddress = { ...prev, [key]: value };
				// Debounced sync to form
				debouncedSyncToForm(newAddress);
				return newAddress;
			});
		},
		[debouncedSyncToForm]
	);

	// Use local state for display
	const addressValue = localAddress;

	return (
		<FormItem>
			<div className="flex flex-wrap justify-between items-center">
				{label?.length && (
					<FormLabel className="flex flex-wrap gap-1">
						<span>{label}</span>
						{required && <span className="align-super text-destructive">*</span>}
					</FormLabel>
				)}
				<FormMessage className="text-xs leading-none" />
			</div>
			<div className="space-y-3 p-4 bg-secondary/40 border rounded">
				<div className="space-y-2">
					<Label className={cn({ 'pointer-events-none': disabled })}>
						Street Address
						{required && <span className="align-super text-destructive ml-1">*</span>}
					</Label>
					<Input
						type="text"
						placeholder="123 Main St"
						value={addressValue.street1 || ''}
						onChange={(e) => updateAddress('street1', e.target.value)}
						disabled={disabled}
						className={cn({ 'opacity-50': disabled })}
					/>
				</div>
				<div className="space-y-2">
					<Label className={cn({ 'pointer-events-none': disabled })}>Apartment, suite, etc. (optional)</Label>
					<Input
						type="text"
						placeholder="Apt 4B"
						value={addressValue.street2 || ''}
						onChange={(e) => updateAddress('street2', e.target.value)}
						disabled={disabled}
						className={cn({ 'opacity-50': disabled })}
					/>
				</div>
				<div className="space-y-2">
					<Label className={cn({ 'pointer-events-none': disabled })}>
						City
						{required && <span className="align-super text-destructive ml-1">*</span>}
					</Label>
					<Input
						type="text"
						placeholder="London"
						value={addressValue.city || ''}
						onChange={(e) => updateAddress('city', e.target.value)}
						disabled={disabled}
						className={cn({ 'opacity-50': disabled })}
					/>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div className="space-y-2">
						<Label className={cn({ 'pointer-events-none': disabled })}>State/Province/Region</Label>
						<Input
							type="text"
							placeholder="Optional"
							value={addressValue.province || ''}
							onChange={(e) => updateAddress('province', e.target.value)}
							disabled={disabled}
							className={cn({ 'opacity-50': disabled })}
						/>
					</div>
					<div className="space-y-2">
						<Label className={cn({ 'pointer-events-none': disabled })}>
							Postal Code
							{required && <span className="align-super text-destructive ml-1">*</span>}
						</Label>
						<Input
							type="text"
							placeholder="SW1A 1AA"
							value={addressValue.postal || ''}
							onChange={(e) => updateAddress('postal', e.target.value)}
							disabled={disabled}
							className={cn({ 'opacity-50': disabled })}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label className={cn({ 'pointer-events-none': disabled })}>
						Country
						{required && <span className="align-super text-destructive ml-1">*</span>}
					</Label>
					<Select
						value={addressValue.country || ''}
						onValueChange={(val) => updateAddress('country', val, true)}
						disabled={disabled}
					>
						<SelectTrigger className={cn({ 'opacity-50': disabled })}>
							<SelectValue placeholder="Select country" />
						</SelectTrigger>
						<SelectContent>
							{COUNTRIES.map(({ value, label }) => (
								<SelectItem key={value} value={value}>
									{label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
			{description && <FormDescription className="mt-2">{description}</FormDescription>}
		</FormItem>
	);
}

export function AddressInternationalBlock<T extends FieldValues>({
	control,
	name,
	label,
	description,
	required,
	disabled
}: {
	control: Control<T, unknown>;
	name: Path<T>;
	label?: string | null;
	description?: string | null;
	required?: boolean;
	disabled?: boolean;
}) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<AddressInternationalBlockInner
					field={field as ControllerRenderProps<FieldValues, string>}
					label={label}
					description={description}
					required={required}
					disabled={disabled}
				/>
			)}
		/>
	);
}
